import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { NARROW_QUERY, useMediaQuery } from './hooks'
import { Icon } from './Icon'

interface BaseColumn<T> {
  key: string
  header: string
  cell: (row: T) => ReactNode
  align?: 'start' | 'end'
}

/** A column of a table that sorts itself (the default, `sortMode: 'local'`). */
export interface Column<T> extends BaseColumn<T> {
  /** Makes the column sortable: the value it sorts by. */
  sortValue?: (row: T) => number | string
  sortable?: never
}

/** A column of a table sorted from outside (`sortMode: 'controlled'`), whose rows arrive sorted. */
export interface ControlledColumn<T> extends BaseColumn<T> {
  /** Makes the column sortable: a header click asks `onSortChange` for it. */
  sortable?: boolean
  sortValue?: never
}

export type SortDirection = 'asc' | 'desc'

export interface TableSort {
  key: string
  direction: SortDirection
}

/** The default: the table sorts its own rows by each column's `sortValue`. */
interface LocalSort<T> {
  sortMode?: 'local'
  columns: readonly Column<T>[]
  initialSort?: TableSort
  sort?: never
  onSortChange?: never
}

/**
 * Sorted from outside: the rows render in the order given (a server sorted them
 * across every page), `sort` is what the headers show, and a header click or the
 * phone sort control asks `onSortChange` for the next sort (`null`: the default
 * order). Its columns say whether they sort with `sortable`; they carry no `sortValue`.
 */
interface ControlledSort<T> {
  sortMode: 'controlled'
  columns: readonly ControlledColumn<T>[]
  sort: TableSort | null
  onSortChange: (sort: TableSort | null) => void
  initialSort?: never
}

export type DataTableProps<T> = {
  /** The table's accessible name. */
  caption: string
  rows: readonly T[]
  rowKey: (row: T) => string
  /** A drill-down per row; the first cell becomes the link. */
  rowHref?: (row: T) => string
  empty?: ReactNode
} & (LocalSort<T> | ControlledSort<T>)

function compare(a: number | string, b: number | string): number {
  return typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))
}

/**
 * Dense, sortable data. On a desktop it is a real table with a sticky first
 * column; at phone width the same rows render as a card list, because a
 * seven-column table scrolled sideways on a phone is not usable.
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const { caption, rows, rowKey, rowHref, empty } = props
  const columns: readonly (Column<T> | ControlledColumn<T>)[] = props.columns
  const controlled = props.sortMode === 'controlled' ? props : null
  const local = props.sortMode === 'controlled' ? null : props
  const [ownSort, setOwnSort] = useState(local?.initialSort ?? null)
  const narrow = useMediaQuery(NARROW_QUERY)
  const sort = controlled ? controlled.sort : ownSort
  const setSort = (next: TableSort | null) => (controlled ? controlled.onSortChange(next) : setOwnSort(next))
  const isSortable = (column: Column<T> | ControlledColumn<T>) => Boolean(controlled ? column.sortable : column.sortValue)

  const sorted = useMemo(() => {
    if (!local || !sort) return rows
    const value = local.columns.find((candidate) => candidate.key === sort.key)?.sortValue
    if (!value) return rows
    const direction = sort.direction === 'desc' ? -1 : 1
    // Negating the comparison, rather than reversing, keeps tied rows in order.
    return [...rows].sort((a, b) => direction * compare(value(a), value(b)))
  }, [rows, sort, local])

  if (rows.length === 0 && empty) return <>{empty}</>

  const toggle = (key: string) =>
    setSort(sort?.key === key ? { key, direction: sort.direction === 'desc' ? 'asc' : 'desc' } : { key, direction: 'desc' })

  const [first, ...rest] = columns
  if (!first) return null

  const firstCell = (row: T) => {
    const href = rowHref?.(row)
    return href ? (
      <Link to={href} className="ci-table__link">
        {first.cell(row)}
      </Link>
    ) : (
      first.cell(row)
    )
  }

  if (narrow) {
    const sortable = columns.filter(isSortable)
    return (
      <div className="ci-table-cards">
        {sortable.length > 0 ? (
          <label className="ci-table-cards__sort">
            <span>Sort by</span>
            <select
              value={sort ? `${sort.key}:${sort.direction}` : ''}
              onChange={(event) => {
                const [key, direction] = event.target.value.split(':')
                setSort(key ? { key, direction: direction === 'asc' ? 'asc' : 'desc' } : null)
              }}
            >
              <option value="">Default order</option>
              {sortable.flatMap((column) => [
                <option key={`${column.key}:desc`} value={`${column.key}:desc`}>
                  {column.header} (high to low)
                </option>,
                <option key={`${column.key}:asc`} value={`${column.key}:asc`}>
                  {column.header} (low to high)
                </option>,
              ])}
            </select>
          </label>
        ) : null}
        <ul aria-label={caption}>
          {sorted.map((row) => (
            <li key={rowKey(row)} className="ci-table-cards__item">
              <div className="ci-table-cards__title">{firstCell(row)}</div>
              <dl>
                {rest.map((column) => (
                  <div key={column.key}>
                    <dt>{column.header}</dt>
                    <dd>{column.cell(row)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div className="ci-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="ci-table">
        <caption className="ci-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort?.key === column.key
              return (
                <th
                  key={column.key}
                  scope="col"
                  data-align={column.align ?? 'start'}
                  aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {isSortable(column) ? (
                    <button type="button" className="ci-table__sort" onClick={() => toggle(column.key)}>
                      {column.header}
                      <Icon name={active ? (sort.direction === 'asc' ? 'arrowUp' : 'arrowDown') : 'sort'} size={12} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)}>
              <th scope="row" data-align={first.align ?? 'start'}>
                {firstCell(row)}
              </th>
              {rest.map((column) => (
                <td key={column.key} data-align={column.align ?? 'start'}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
